import { createContext, useContext } from 'react';
import RepeatGroup from './RepeatGroup.jsx';

// The flow supplies its "go back" handler here so Back sits right next to Continue
// on every question (null when there's nowhere to go back to — e.g. the first screen).
export const BackContext = createContext(null);

// Continue button, paired with Back so navigation lives in one obvious spot.
export function ContinueButton({ label = 'Continue', onClick, disabled }) {
  const onBack = useContext(BackContext);
  return (
    <div className="ctafooter">
      {onBack && (
        <button type="button" className="ctaback" onClick={onBack}>Back</button>
      )}
      <div className="ctaw">
        <button className="cta" onClick={onClick} disabled={disabled}>{label}</button>
      </div>
    </div>
  );
}

// Back on its own — for auto-advancing screens (Yes/No, single-select) that have no
// Continue button, so Back stays in the same spot on every screen. Renders nothing
// when there's nowhere to go back to.
export function BackButton() {
  const onBack = useContext(BackContext);
  if (!onBack) return null;
  return (
    <div className="ctafooter backonly">
      <button type="button" className="ctaback" onClick={onBack}>Back</button>
    </div>
  );
}

// Small delay before auto-advancing so the selection paints first.
export function advanceSoon(onAdvance) {
  setTimeout(onAdvance, 200);
}

// Subtle "this is required" hint, shown beneath a required input while it's empty.
// Free-text answers may be "none" (per the required-review rule), so text inputs say
// so — otherwise a 40–75 client may think a not-applicable question blocks them.
export function RequiredNote({ textAnswer }) {
  return (
    <p className="reqnote">
      {textAnswer ? 'Required — type “none” if it doesn’t apply.' : 'Required'}
    </p>
  );
}

// Inline follow-up revealed beneath a Yes/No when the answer is Yes. Either a single
// free-text box or a repeatable group (surgeries). Its answer keeps its own field id.
export function Followup({ fu, answers, setAnswer, onAdvance }) {
  if (fu.repeat) {
    return <RepeatGroup fu={fu} answers={answers} setAnswer={setAnswer} onAdvance={onAdvance} />;
  }
  const val = answers[fu.id] || '';
  // A roll-up is required only when it's shown (parent = Yes), which is exactly here.
  const incomplete = fu.required_when_shown && !val.trim();
  return (
    <div>
      <p className="sub" style={{ margin: '18px 0 8px' }}>{fu.label}</p>
      <textarea className="ta" value={val} onChange={(e) => setAnswer(fu.id, e.target.value)} />
      {incomplete && <RequiredNote textAnswer />}
      <div className="spacer" style={{ height: 14 }} />
      <ContinueButton onClick={onAdvance} disabled={incomplete} />
    </div>
  );
}
